import axios from 'axios';
import type { Product, ProductColor, ProductColorOption, ProductSize, ProductSpec, ProductVariant } from '@/types';

const websiteApiBaseUrl = (import.meta.env.VITE_WEBSITE_API_BASE_URL || 'https://api.beautysports.in/website-api').replace(/\/$/, '');
const websiteAssetBaseUrl = websiteApiBaseUrl.replace(/\/website-api$/, '');

function resolveImageUrl(image?: string) {
  if (!image) return '';
  if (/^https?:\/\//i.test(image)) return image;
  if (image.startsWith('/uploads')) return websiteAssetBaseUrl + image;
  return image;
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.map((item) => String(item)).filter(Boolean) : [];
}

function list<T>(value: unknown): T[] {
  return Array.isArray(value) ? value as T[] : [];
}

function imageValue(value: unknown) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const item = value as Record<string, unknown>;
    return String(item.image || item.url || item.src || item.path || item.thumbnail || '');
  }
  return '';
}

function resolveImageList(value: unknown) {
  return list<unknown>(value).map(imageValue).map(resolveImageUrl).filter(Boolean);
}

function genderValue(value: unknown): Product['gender'] {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'male' || normalized === 'men') return 'men';
  if (normalized === 'female' || normalized === 'women') return 'women';
  if (normalized === 'kids') return 'kids';
  return 'unisex';
}

function ageGroupValue(value: unknown): Product['ageGroup'] {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'youth') return 'youth';
  if (normalized === 'kids') return 'kids';
  return 'adult';
}

function mapColorOption(option: Partial<ProductColorOption>): ProductColorOption {
  return {
    name: String(option.name || ''),
    image: resolveImageUrl(option.image),
    available: Boolean(option.available),
    sizes: list<ProductSize>(option.sizes),
  };
}

function mapVariant(variant: Partial<ProductVariant> & { uuid?: string }): ProductVariant {
  const thumbnail = resolveImageUrl(variant.thumbnail);

  return {
    id: String(variant.id || variant.uuid || ''),
    uuid: String(variant.uuid || variant.id || ''),
    name: String(variant.name || ''),
    colorName: String(variant.colorName || ''),
    sizeLabel: variant.sizeLabel ? String(variant.sizeLabel) : undefined,
    sizeValue: variant.sizeValue ? String(variant.sizeValue) : undefined,
    fit: variant.fit ? String(variant.fit) : undefined,
    material: variant.material ? String(variant.material) : undefined,
    sku: variant.sku ? String(variant.sku) : undefined,
    price: Number(variant.price || 0),
    originalPrice: Number(variant.originalPrice || variant.price || 0),
    description: String(variant.description || ''),
    thumbnail,
    images: Array.from(new Set([thumbnail, ...resolveImageList(variant.images)].filter(Boolean))),
    inStock: Boolean(variant.inStock),
    stockCount: Number(variant.stockCount || 0),
  };
}

export type ProductApiItem = Partial<Product> & {
  uuid?: string;
  similarProducts?: ProductApiItem[];
  image1?: string;
  image2?: string;
};

export function mapWebsiteProduct(item: ProductApiItem): Product {
  const variants = list<Partial<ProductVariant>>(item.variants).map(mapVariant);
  const image1 = resolveImageUrl(item.image1 || item.thumbnail);
  const image2 = resolveImageUrl(item.image2 || item.hoverImage) || image1;
  const thumbnail = image1;
  const hoverImage = image2;

  return {
    id: String(item.id || item.uuid || ''),
    uuid: String(item.uuid || item.id || ''),
    name: String(item.name || ''),
    slug: String(item.slug || item.id || item.uuid || ''),
    brand: String(item.brand || ''),
    category: String(item.category || ''),
    subCategory: String(item.subCategory || ''),
    subCategorySlug: String(item.subCategorySlug || ''),
    categorySlug: String(item.categorySlug || ''),
    sport: String(item.sport || item.category || ''),
    description: String(item.description || ''),
    shortDescription: String(item.shortDescription || item.description || ''),
    price: Number(item.price || 0),
    originalPrice: Number(item.originalPrice || item.price || 0),
    discount: Number(item.discount || 0),
    currency: String(item.currency || 'INR'),
    images: resolveImageList(item.images),
    variants,
    thumbnail,
    hoverImage,
    image1: item.image1 || item.thumbnail,
    image2: item.image2 || item.hoverImage,
    rating: Number(item.rating || 0),
    reviewCount: Number(item.reviewCount || 0),
    inStock: Boolean(item.inStock),
    stockCount: Number(item.stockCount || 0),
    sizes: list<ProductSize>(item.sizes),
    colors: list<ProductColor>(item.colors),
    colorOptions: list<Partial<ProductColorOption>>(item.colorOptions).map(mapColorOption).filter((option) => option.name),
    sizeChart: String(item.sizeChart || ''),
    sizeChartUuid: String(item.sizeChartUuid || ''),
    sizeChartType: String(item.sizeChartType || ''),
    sizeChartImage: resolveImageUrl(String(item.sizeChartImage || '')),
    features: stringList(item.features),
    specifications: list<ProductSpec>(item.specifications),
    material: String(item.material || ''),
    gender: genderValue(item.gender),
    ageGroup: ageGroupValue(item.ageGroup),
    tags: stringList(item.tags),
    isNew: Boolean(item.isNew),
    isBestSeller: Boolean(item.isBestSeller),
    isFeatured: Boolean(item.isFeatured),
    createdAt: String(item.createdAt || ''),
  };
}

function cleanProducts(items: ProductApiItem[] = []): Product[] {
  return items.map(mapWebsiteProduct).filter((product) => product.id && product.name && product.thumbnail);
}

export interface WebsiteProductQuery {
  category?: string;
  categoryUuid?: string;
  subCategory?: string;
  subCategorySlug?: string;
  brand?: string;
  brandUuid?: string;
  discount?: string;
  sale?: boolean;
  search?: string;
  q?: string;
  sort?: string;
}

export type WebsiteProductDetails = Product;

function queryParams(query: WebsiteProductQuery = {}) {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    params.set(key, String(value));
  });
  return params.toString();
}

export async function getWebsiteProducts(query: WebsiteProductQuery = {}): Promise<Product[]> {
  const params = queryParams(query);
  const url = params ? websiteApiBaseUrl + '/products?' + params : websiteApiBaseUrl + '/products';
  const response = await axios.get<{ data: ProductApiItem[] }>(url);
  return cleanProducts(response.data.data || []);
}

export async function getWebsiteProductSearch(search: string, limit = 12): Promise<Product[]> {
  const term = search.trim();
  if (!term) return [];
  const params = queryParams({ q: term });
  const url = websiteApiBaseUrl + '/products/search?' + params + '&limit=' + encodeURIComponent(String(limit));
  const response = await axios.get<{ data: ProductApiItem[] }>(url);
  return cleanProducts(response.data.data || []);
}

export async function getWebsitePopularProducts(): Promise<Product[]> {
  const response = await axios.get<{ data: ProductApiItem[] }>(websiteApiBaseUrl + '/products/popular');
  return cleanProducts(response.data.data || []);
}

export async function getWebsiteCategoryLatestProducts(categoryUuidOrSlug: string): Promise<Product[]> {
  const response = await axios.get<{ data: ProductApiItem[] }>(websiteApiBaseUrl + '/categories/' + encodeURIComponent(categoryUuidOrSlug) + '/products/latest');
  return cleanProducts(response.data.data || []);
}

export async function getWebsiteBrandLatestProducts(brandUuidOrSlug: string): Promise<Product[]> {
  const response = await axios.get<{ data: ProductApiItem[] }>(websiteApiBaseUrl + '/brands/' + encodeURIComponent(brandUuidOrSlug) + '/products/latest');
  return cleanProducts(response.data.data || []);
}

export async function getWebsiteBrandProducts(brandUuidOrSlug: string, query: WebsiteProductQuery = {}): Promise<Product[]> {
  const params = queryParams(query);
  const url = websiteApiBaseUrl + '/brands/' + encodeURIComponent(brandUuidOrSlug) + '/products' + (params ? '?' + params : '');
  const response = await axios.get<{ data: ProductApiItem[] }>(url);
  return cleanProducts(response.data.data || []);
}

export async function getWebsiteSubcategoryProducts(subCategoryUuidOrSlug: string, query: WebsiteProductQuery = {}): Promise<Product[]> {
  const params = queryParams(query);
  const url = websiteApiBaseUrl + '/subcategories/' + encodeURIComponent(subCategoryUuidOrSlug) + '/products' + (params ? '?' + params : '');
  const response = await axios.get<{ data: ProductApiItem[] }>(url);
  return cleanProducts(response.data.data || []);
}

export async function getWebsiteSubcategoryLatestProducts(subCategoryUuidOrSlug: string): Promise<Product[]> {
  const response = await axios.get<{ data: ProductApiItem[] }>(websiteApiBaseUrl + '/subcategories/' + encodeURIComponent(subCategoryUuidOrSlug) + '/products/latest');
  return cleanProducts(response.data.data || []);
}

export async function getWebsiteDiscountProducts(discountUuid: string, query: Omit<WebsiteProductQuery, 'discount'> = {}): Promise<Product[]> {
  const params = queryParams(query);
  const url = websiteApiBaseUrl + '/discounts/' + encodeURIComponent(discountUuid) + '/products' + (params ? '?' + params : '');
  const response = await axios.get<{ data: ProductApiItem[] }>(url);
  return cleanProducts(response.data.data || []);
}

export async function getWebsiteProduct(slugOrUuid: string): Promise<WebsiteProductDetails> {
  const response = await axios.get<{ data: ProductApiItem }>(websiteApiBaseUrl + '/products/' + encodeURIComponent(slugOrUuid));
  const item = response.data.data || {};
  return mapWebsiteProduct(item);
}

export async function getWebsiteSimilarProducts(slugOrUuid: string): Promise<Product[]> {
  const response = await axios.get<{ data: ProductApiItem[] }>(websiteApiBaseUrl + '/products/' + encodeURIComponent(slugOrUuid) + '/similar');
  return cleanProducts(response.data.data || []);
}

export async function getWebsiteFeaturedProducts(): Promise<Product[]> {
  const response = await axios.get<{ data: ProductApiItem[] }>(websiteApiBaseUrl + '/products/featured');
  const rawItems = response.data.data || [];
  return rawItems.map((item) => {
    const product = mapWebsiteProduct(item);
    return {
      ...product,
      customTitle: (item as Record<string, unknown>).customTitle as string || '',
      featuredImage: resolveImageUrl((item as Record<string, unknown>).featuredImage as string || ''),
    };
  }).filter((p) => p.id && p.name && p.thumbnail);
}

export interface BrowseProductsResponse {
  products: Product[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

export async function getWebsiteBrowseProducts(page = 1, limit = 20): Promise<BrowseProductsResponse> {
  const response = await axios.get<{ products: ProductApiItem[]; page: number; limit: number; total: number; hasMore: boolean }>(
    websiteApiBaseUrl + '/products/browse?page=' + page + '&limit=' + limit,
  );
  const data = response.data;
  return {
    products: cleanProducts(data.products || []),
    page: data.page || page,
    limit: data.limit || limit,
    total: data.total || 0,
    hasMore: Boolean(data.hasMore),
  };
}
